import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { colors, typography, spacing } from '../../theme';
import { useAsyncAnalysis } from '../../hooks/useAsyncAnalysis';
import { validateText } from '../../utils/text-validation';
import { validateUrl } from '../../utils/url-validation';
import { validateFile } from '../../utils/file-validation';
import type { AnalysisSession, SourceAssessmentResult } from '../../types/analysis-session';
import { InputPreview } from '../PipelineAnalysis/sections/InputPreview';
import { SourceResults } from '../PipelineAnalysis/sections/SourceResults';
import { VerdictSection } from '../PipelineAnalysis/styles';

// =============================================================================
// Input Types
// =============================================================================

type InputType = 'text' | 'image' | 'audio' | 'video' | 'url';

const INPUT_TYPES: { key: InputType; labelKey: string; enabled: boolean; accept?: string }[] = [
  { key: 'text', labelKey: 'common.text', enabled: true },
  { key: 'image', labelKey: 'common.image', enabled: true, accept: 'image/jpeg,image/png,image/webp,image/gif' },
  { key: 'audio', labelKey: 'common.audio', enabled: true, accept: 'audio/mpeg,audio/wav,audio/ogg,audio/mp4' },
  { key: 'video', labelKey: 'common.video', enabled: true, accept: 'video/mp4,video/webm,video/ogg' },
  { key: 'url', labelKey: 'common.url', enabled: true },
];

// =============================================================================
// Component
// =============================================================================

export const DomainAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const async = useAsyncAnalysis('source-assessment');
  const [inputType, setInputType] = useState<InputType>('text');
  const [inputText, setInputText] = useState('');
  const [inputUrl, setInputUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [parsedResult, setParsedResult] = useState<SourceAssessmentResult | null>(null);
  const [analyzedInput, setAnalyzedInput] = useState<{ type: InputType; text: string } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!async.result) return;
    try {
      const session: AnalysisSession = async.result;
      if (session.source_assessment) {
        setParsedResult(session.source_assessment);
      }
      if (session.input_text && session.input_type !== 'text' && session.input_type !== 'url') {
        setAnalyzedInput(prev => prev ? { ...prev, text: session.input_text! } : prev);
      }
    } catch {
      // parsing failed
    }
  }, [async.result]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateFile(file, inputType);
    if (!validation.valid) {
      setFileError(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTypeChange = (type: InputType) => {
    setInputType(type);
    setSelectedFile(null);
    setFileError(null);
    setParsedResult(null);
    setAnalyzedInput(null);
    async.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const textValidation = useMemo(() => validateText(inputText), [inputText]);
  const urlValidation = useMemo(() => validateUrl(inputUrl), [inputUrl]);

  const canAnalyze =
    inputType === 'text' ? textValidation.valid :
    inputType === 'url' ? urlValidation.valid :
    selectedFile !== null;

  const handleAnalyze = () => {
    if (!canAnalyze) return;
    setParsedResult(null);

    if (inputType === 'text') {
      const txt = inputText.trim();
      setAnalyzedInput({ type: inputType, text: txt });
      async.submitText(txt);
    } else if (inputType === 'url') {
      const url = inputUrl.trim();
      setAnalyzedInput({ type: inputType, text: url });
      async.submitUrl(url);
    } else if (selectedFile) {
      setAnalyzedInput({ type: inputType, text: selectedFile.name });
      async.submitMedia(selectedFile, inputType);
    }
  };

  const isAnalyzing = async.isAnalyzing;
  const error = async.error;
  const r = parsedResult;

  return (
    <Container>
      <Header>
        <Title>{t('domain.title')}</Title>
        <Subtitle>{t('domain.subtitle')}</Subtitle>
      </Header>

      {/* Input Type Tabs */}
      <TypeTabs>
        {INPUT_TYPES.filter(it => it.enabled).map((it) => (
          <TypeTab
            key={it.key}
            active={inputType === it.key}
            onClick={() => handleTypeChange(it.key)}
          >
            {t(it.labelKey)}
          </TypeTab>
        ))}
      </TypeTabs>

      {/* Input Area */}
      <InputArea>
        {inputType === 'text' && (
          <>
            <TextArea
              placeholder={t('domain.textPlaceholder')}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={isAnalyzing}
              rows={6}
            />
            {inputText.trim() && textValidation.error && (
              <ValidationMsg color="#ef4444">{textValidation.error}</ValidationMsg>
            )}
            {inputText.trim() && textValidation.valid && (
              <ValidationMsg color="#22c55e">Valid ({inputText.trim().length} chars)</ValidationMsg>
            )}
          </>
        )}

        {inputType === 'url' && (
          <>
            <UrlInput
              type="text"
              placeholder={t('domain.urlPlaceholder')}
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canAnalyze && !isAnalyzing) handleAnalyze(); }}
              disabled={isAnalyzing}
            />
            {inputUrl.trim() && urlValidation.error && (
              <ValidationMsg color="#ef4444">{urlValidation.error}</ValidationMsg>
            )}
            {inputUrl.trim() && urlValidation.valid && (
              <ValidationMsg color="#22c55e">{t('common.validUrl')}</ValidationMsg>
            )}
          </>
        )}

        {(inputType === 'image' || inputType === 'audio' || inputType === 'video') && (
          <>
            <FileInputArea>
              <input
                ref={fileInputRef}
                type="file"
                accept={INPUT_TYPES.find(t => t.key === inputType)?.accept}
                onChange={handleFileSelect}
                disabled={isAnalyzing}
                style={{ display: 'none' }}
              />
              {selectedFile ? (
                <FileSelected>
                  <FileName>{selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</FileName>
                  <RemoveFileBtn onClick={handleRemoveFile}>Remove</RemoveFileBtn>
                </FileSelected>
              ) : (
                <FileDropZone onClick={() => fileInputRef.current?.click()}>
                  Click to select {inputType} file
                </FileDropZone>
              )}
            </FileInputArea>
            {fileError && <ValidationMsg color="#ef4444">{fileError}</ValidationMsg>}
          </>
        )}

        <AnalyzeBtn onClick={handleAnalyze} disabled={isAnalyzing || !canAnalyze}>
          {isAnalyzing ? <><Spinner /> {async.statusText || t('common.analyzing')}</> : t('domain.analyzeSource')}
        </AnalyzeBtn>
      </InputArea>

      {error && <ErrorBox><ErrorIcon>!</ErrorIcon>{error}</ErrorBox>}

      {async.warnings.length > 0 && (
        <WarningBox>{async.warnings.map((w, i) => <div key={i}>{w}</div>)}</WarningBox>
      )}

      {/* Input Preview (shown once result lands) */}
      {r && analyzedInput && (
        <InputPreview inputType={analyzedInput.type} text={analyzedInput.text} />
      )}

      {/* Results — editorial layout */}
      {r && (
        <VerdictSection>
          <SourceResults result={r} />
        </VerdictSection>
      )}
    </Container>
  );
};

// =============================================================================
// Styled Components — input form & banners (results use shared editorial)
// =============================================================================

const Container = styled.div`
  width: 100%; max-width: 1800px; margin: 0 auto;
  padding: 0;
  @media (max-width: 768px) { padding: 0; }
`;
const Header = styled.div`margin-bottom: ${spacing.xl}px;`;

const Title = styled.h1`
  font-family: ${typography.fontFamily.primary}; font-size: 28px;
  font-weight: ${typography.fontWeight.bold}; color: #ffffff; margin: 0 0 ${spacing.xs}px 0;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;

const Subtitle = styled.p`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.5); margin: 0;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;

/* Input Type Tabs */
const TypeTabs = styled.div`
  display: flex; gap: 4px; margin-bottom: ${spacing.lg}px;
  background: rgba(255, 255, 255, 0.03); border-radius: 10px; padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  [data-theme="light"] & { background: rgba(0,0,0,0.02); border-color: ${colors.lightGray}; }
  @media (max-width: 480px) { flex-wrap: wrap; }
`;

const TypeTab = styled.button<{ active?: boolean }>`
  flex: 1; padding: 8px 12px; border: none; border-radius: 8px; cursor: pointer;
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  font-weight: ${p => p.active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  transition: all 0.2s;
  background: ${p => p.active ? 'rgba(139, 92, 246, 0.15)' : 'transparent'};
  color: ${p => p.active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
  [data-theme="light"] & {
    background: ${p => p.active ? 'rgba(139, 92, 246, 0.1)' : 'transparent'};
    color: ${p => p.active ? colors.slateCharcoal : 'rgba(0,0,0,0.5)'};
  }
  &:hover { background: rgba(139, 92, 246, 0.1); }
`;

/* Input Area */
const InputArea = styled.div`
  display: flex; flex-direction: column; gap: ${spacing.md}px; margin-bottom: ${spacing.lg}px;
`;

const TextArea = styled.textarea`
  width: 100%; padding: 12px ${spacing.lg}px; min-height: 120px; resize: vertical;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px; font-family: ${typography.fontFamily.primary}; font-size: 14px;
  color: #ffffff; line-height: 1.6; transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(139, 92, 246, 0.4); }
  &::placeholder { color: rgba(255, 255, 255, 0.25); }
  [data-theme="light"] & {
    background: white; border-color: ${colors.lightGray}; color: ${colors.slateCharcoal};
    &:focus { border-color: ${colors.neonViolet}; }
    &::placeholder { color: ${colors.hintGray}; }
  }
`;

const UrlInput = styled.input`
  width: 100%; padding: 12px ${spacing.lg}px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px; font-family: ${typography.fontFamily.primary}; font-size: 15px;
  color: #ffffff; transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(139, 92, 246, 0.4); }
  &::placeholder { color: rgba(255, 255, 255, 0.25); }
  [data-theme="light"] & {
    background: white; border-color: ${colors.lightGray}; color: ${colors.slateCharcoal};
    &:focus { border-color: ${colors.neonViolet}; }
    &::placeholder { color: ${colors.hintGray}; }
  }
`;

const FileInputArea = styled.div``;

const FileDropZone = styled.div`
  padding: 32px; text-align: center; border: 2px dashed rgba(139, 92, 246, 0.2);
  border-radius: 10px; cursor: pointer; color: rgba(255, 255, 255, 0.4);
  font-size: ${typography.fontSize.sm}; transition: all 0.2s;
  &:hover { border-color: rgba(139, 92, 246, 0.4); background: rgba(139, 92, 246, 0.03); }
  [data-theme="light"] & { border-color: rgba(139, 92, 246, 0.15); color: ${colors.steelGray};
    &:hover { border-color: rgba(139, 92, 246, 0.3); }
  }
`;

const FileSelected = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 12px ${spacing.lg}px; background: rgba(139, 92, 246, 0.06);
  border: 1px solid rgba(139, 92, 246, 0.15); border-radius: 10px;
`;

const FileName = styled.span`
  font-size: ${typography.fontSize.sm}; color: rgba(255, 255, 255, 0.7);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;

const RemoveFileBtn = styled.button`
  padding: 4px 12px; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 6px; color: #f87171; font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { background: rgba(239, 68, 68, 0.2); }
`;

const ValidationMsg = styled.div<{ color: string }>`
  font-size: 12px; color: ${p => p.color}; padding-left: 2px;
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Spinner = styled.span`
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid var(--accent-subtle); border-top-color: var(--fg-on-accent);
  border-radius: 50%; animation: ${spin} 0.7s linear infinite;
`;

const AnalyzeBtn = styled.button<{ disabled?: boolean }>`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 28px; align-self: flex-start;
  background: ${p => p.disabled ? 'rgba(139, 92, 246, 0.15)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)'};
  color: ${p => p.disabled ? 'var(--fg-disabled)' : 'var(--fg-on-accent)'};
  border: none; border-radius: 10px;
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'}; transition: all 0.2s; white-space: nowrap;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(139, 92, 246, 0.35); }
  @media (max-width: 480px) { width: 100%; }
`;

const ErrorBox = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px ${spacing.lg}px; background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2); border-radius: 10px;
  color: #f87171; font-size: ${typography.fontSize.sm}; margin-bottom: ${spacing.lg}px;
  [data-theme="light"] & { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
`;

const WarningBox = styled.div`
  padding: 12px ${spacing.lg}px; background: rgba(234, 179, 8, 0.08);
  border: 1px solid rgba(234, 179, 8, 0.2); border-radius: 10px;
  color: #eab308; font-size: ${typography.fontSize.sm}; margin-bottom: ${spacing.lg}px;
  display: flex; flex-direction: column; gap: 4px;
  [data-theme="light"] & { background: #fefce8; border-color: #fde68a; color: #a16207; }
`;

const ErrorIcon = styled.span`
  display: flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 50%;
  background: rgba(239, 68, 68, 0.2); font-size: 11px; font-weight: 700; flex-shrink: 0;
`;
